/**
 * This page differs from the AuthResetPage in that the AuthOperationPage is
 * for the User to initiate some sort of authentication support request. This
 * could be a password/phone reset or a resend of the email verification email.
 * The AuthResetPage is only accessible via a restricted link sent in the email.
 */

import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import { Navbar } from "@avlino-inc/alenza-react";
import {
  resetOperationEmailNoAuthAction,
  logoutUserAction,
} from "store/actions/actionCreators";
import ClipLoader from "react-spinners/ClipLoader";
import { override } from "components/spinner/spinnerOverride";
import { validEmail } from "util/formValidators";
import { getTheme } from "lib/theme";
import { ThemeLogo } from "components/pages/auth/ThemeLogo";
import genStyles from "styles/General.module.scss";
import styles from "./Auth.module.scss";
import { ResetOperationEmailNoAuthRequestType } from "types";
import { getResetAuthReducerResponse } from "store/selectors";
import * as t from "types";
import * as routes from "routes";

const AuthOperationPage = ({ query }: { query: string }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [email, setEmail] = useState<string>();
  const [sendingEmail, setSendingEmail] = useState<boolean>(false);
  const [theme, setTheme] = useState<string>();

  // From store
  const resetAuthData: t.BasicPostDataDeserializerType = useSelector((state) =>
    getResetAuthReducerResponse(state, "data")
  );
  const resetAuthError: t.BasicErrorDeserializerType = useSelector((state) =>
    getResetAuthReducerResponse(state, "error")
  );

  // NOTE: Added wasSubmit to prevent redirecting to login page immediatly
  const [wasSubmit, setWasSubmit] = useState(false);

  const onHandleResetRequest = (event: any) => {
    event.preventDefault();
    setSendingEmail(true);
    if (email) {
      let request: ResetOperationEmailNoAuthRequestType = {
        body: {
          email,
          op_type: "",
        },
      };
      if (location.pathname.includes("forgotten-password")) {
        log.info("Send Password Reset Email...");
        request.body.op_type = "password_reset";
      } else if (location.pathname.includes("forgotten-phone")) {
        log.info("Send Phone Reset Email...");
        request.body.op_type = "phone_reset";
      } else {
        log.info("Resending Verification Email...");
        request.body.op_type = "email_verification";
      }
      dispatch(resetOperationEmailNoAuthAction(request));
      dispatch(logoutUserAction({}));
      setWasSubmit(true);
      history.push(routes.LOGIN);
    }
  };

  const generateButtonLabel = () => {
    if (location.pathname.includes("forgotten-password")) {
      return "Reset Password";
    } else if (location.pathname.includes("forgotten-phone")) {
      return "Reset Phone";
    } else {
      return "Resend Email";
    }
  };

  const onClickUserCard = useCallback(() => history.push(routes.LOGIN), []);

  useEffect(() => {
    log.info("AuthLoginPage useEffect()...");
    setTheme(getTheme());
  }, []);

    /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);

  useEffect(() => {
    if (!wasSubmit) return;
    if (resetAuthData && resetAuthData.success) {
      history.push(routes.LOGIN);
    }
  }, [wasSubmit, resetAuthData, resetAuthError, history]);

  return (
    <div id="AuthOperationPage" className={styles["auth-page"]}>
      <Navbar
					showLogo
					logoUrl='/'
          productDefinition = {
            {
              productName:'Alenza'
            }
          }
					productNameUrl='/'
					dropdown={undefined}
					notificationItems={undefined}
          onClickUserCard={onClickUserCard}
          email={""}
      />
      <Container className={styles["auth-page__container"]} fluid={true}>
        <Row className={styles["auth-page__container__row"]}>
          <Col md={12} className={styles["auth-page__container__row__content"]}>
            <Card
              className={
                styles["auth-page__container__row__content__auth-card"]
              }
            >
              <Card.Body className="text-center">
                <div>
                  <ThemeLogo theme={theme} />
                </div>
                <div>
                  <form name="create-user-form" onSubmit={onHandleResetRequest}>
                    <div className="w-100">
                      <input
                        type="email"
                        name="email"
                        placeholder="Email"
                        className={[genStyles["alenza__input"], "w-75"].join(
                          " "
                        )}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setEmail(e.target.value);
                        }}
                      />
                      <Button
                        type="submit"
                        className={[
                          genStyles["alenza__btn-primary"],
                          "w-75",
                        ].join(" ")}
                        disabled={!validEmail(email) || sendingEmail}
                        onClick={(e: any) => {
                          onHandleResetRequest(e);
                        }}
                      >
                        {sendingEmail ? (
                          <ClipLoader
                            color={"#ffffff"}
                            css={override}
                            size={14}
                            loading={sendingEmail}
                          />
                        ) : (
                          generateButtonLabel()
                        )}
                      </Button>
                    </div>
                  </form>
                </div>
              </Card.Body>
              <Card.Footer>
                <div style={{ marginBottom: "15px" }}>
                  <div
                    className={genStyles["link"]}
                    onClick={(e) => history.push(routes.LOGIN)}
                  >
                    Back to Login
                  </div>
                </div>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AuthOperationPage;
